import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { filterNotEmpty } from 'utils/data/listHelpers';
import { SettingsServiceContext } from 'common/settingsService/impl/provider/SettingsServiceContext';
import { SettingsServiceTypes } from 'common/settingsService/types';
import { useSettingsServiceChannel } from 'common/settingsService/impl/broadcastChannel/useSettingsServiceChannel';
import { FETCH_POLICY } from 'common/apollo/FETCH_POLICY';
import { useSettingsServiceCache } from 'common/settingsService/impl/cache/useSettingsServiceCache';
import { SettingsServiceState } from 'common/settingsService/impl/models/SettingsServiceState';
import { SettingsGroup } from 'common/settingsService/impl/models/SettingsGroup';
import { settingsSources as sources } from 'common/settingsService/settingsSources';
import { logger } from 'src/utils/logger';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { useFeatureFlags } from 'common/featureFlag/useFeatureFlags';

interface SettingsServiceProviderProps {
  children: React.ReactNode;
  fetch: boolean;
}

const isTestEnv = process.env.NODE_ENV === 'test';
export const SettingsServiceProvider = ({ children, fetch }: SettingsServiceProviderProps) => {
  const { query } = useApolloClient();
  const { setCache, getCache } = useSettingsServiceCache();
  const flagsLD = useDarkly();
  const [loading, setLoading] = useState(false);
  const { isEnabled: isTenantFlagEnabled } = useFeatureFlags(true);

  const [state, setLocally] = useReducer(
    (prevState: SettingsServiceTypes.StateModel, action: SettingsServiceTypes.StateModel) => ({
      ...prevState,
      ...action
    }),
    {},
    () => {
      // merge settings state from SettingSource and from the cache and serve it
      const cacheState = SettingsServiceState.fromStateModel(getCache());
      const initialState = SettingsServiceState.fromArrayOfGroups(sources.map((it) => it.state));
      return initialState.merge(cacheState).model;
    }
  );

  const saveState = useCallback(
    (nextState: SettingsServiceTypes.StateModel) => {
      setCache(nextState);
      setLocally(nextState);
    },
    [setCache]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (!fetch || isTestEnv) {
      return;
    }
    setLoading(true);
    // make initial call in order to get latest flags from BE
    Promise.allSettled(
      sources.map(({ document }) => {
        if (
          !document.featureFlags ||
          document.featureFlags.every((flag) =>
            flag.source === 'LaunchDarkly' ? !!flagsLD[flag.name] : isTenantFlagEnabled(flag.name)
          )
        ) {
          return query({ query: document.query, fetchPolicy: FETCH_POLICY.NO_CACHE, variables: document.variables });
        }
        return true;
      })
    ).then((responses: PromiseSettledResult<any>[]) => {
      try {
        const nextState = SettingsServiceState.fromArrayOfGroups(
          filterNotEmpty(
            sources.map((source, index) => {
              const response = responses[index] ?? { status: 'rejected' };
              if (response.status === 'rejected') {
                logger.warn(`Request ${(source.document.query.definitions?.[0] as any)?.name?.value} failed`);
                return undefined;
              }
              const flagsModel = source.document.mapper(response.value) ?? {};
              const receivedKeys = Object.keys(flagsModel ?? {});
              const flagSourceKeys = Object.keys(source.model.items ?? {});
              if (receivedKeys.length !== flagSourceKeys.length && !!sessionStorage.getItem('verbose')) {
                logger.warn(`
                  Number of flags received differs from the SettingsSource Class. Ignore this message if that is intended. \n
                  Flags from BE (${receivedKeys.length}): [${receivedKeys.sort().join(', ')}]\n
                  Flags from class (${flagSourceKeys.length}): [${flagSourceKeys.sort().join(', ')}]\n
                  Not covered flags: [${listUniqueFlags(receivedKeys, flagSourceKeys).sort().join(', ')}]
                  `);
              }
              return SettingsGroup.fromItemsModel(source.model.name, flagsModel);
            })
          )
        ).model;
        saveState(nextState);
        setLoading(false);
        return nextState;
      } catch (e) {
        setLoading(false);
        return {};
      }
    });
  }, [fetch]);

  const handleChannelEvent = useCallback(
    (event: SettingsServiceTypes.ChannelEvent) => {
      logger.groupCollapsed(`Flags received from broadcast channel: ${Object.keys(event.data).join(', ')}`);
      logger.log(event.data);
      logger.groupEnd();
      saveState(event.data);
    },
    [saveState]
  );

  const broadcast = useSettingsServiceChannel(handleChannelEvent);

  const broadcastFlags: SettingsServiceTypes.ContextState['broadcast'] = useCallback(
    (groupKey, flagsModel) => {
      const nextState = SettingsServiceState.fromStateModel(state).mergeGroup(
        SettingsGroup.fromItemsModel(groupKey, flagsModel)
      ).model;
      broadcast(nextState);
      saveState(nextState);
    },
    [broadcast, saveState, state]
  );

  const contextValue: SettingsServiceTypes.ContextState = useMemo(
    () => ({ state, broadcast: broadcastFlags, loading }),
    [state, broadcastFlags, loading]
  );
  return <SettingsServiceContext.Provider value={contextValue}>{children}</SettingsServiceContext.Provider>;
};

function listUniqueFlags(list1: string[], list2: string[]): string[] {
  const map = new Map<string, number>();
  for (const name of [...list1, ...list2]) {
    if (map.has(name)) {
      map.set(name, (map.get(name) ?? 1) + 1);
    } else {
      map.set(name, 1);
    }
  }
  return Array.from(map.keys()).filter((name) => map.get(name) === 1);
}
